import {
  Control,
  Controller,
  ControllerRenderProps,
  FieldValues,
  get,
  Path,
  PathValue,
  useFormState,
} from "react-hook-form";
import { useRef, useState } from "react";
import EyeIcon from "@/assets/icons/eye.svg?react";
import EyeOffIcon from "@/assets/icons/eye-off.svg?react";
import InformationCircleIcon from "@/assets/icons/info-circle.svg?react";
import { Field, Input, Label } from "@headlessui/react";

import clsx from "clsx";
import { useTranslateMessage } from "@/hooks/useTranslateMessage";

interface BaseProps<FieldsType extends FieldValues> {
  title?: string;
  name: Path<FieldsType>;
  control: Control<FieldsType>;
  defaultValue?: PathValue<FieldsType, Path<FieldsType>>;
  className?: React.HTMLAttributes<HTMLDivElement>["className"];
  disabled?: boolean;
  tooltip?: string;
  rightElement?: React.ReactNode;
  isReadOnly?: boolean;
}

type InputFormProps<FieldsType extends FieldValues> =
  | ({
      type: "number";
      isFloat?: boolean;
    } & BaseProps<FieldsType>)
  | ({
      type?: Exclude<React.HTMLInputTypeAttribute, "number">;
      isFloat?: never;
    } & BaseProps<FieldsType>);

const StyledInput = <FieldsType extends FieldValues>({
  title,
  control,
  name,
  type = "text",
  isFloat = false,
  defaultValue,
  className,
  disabled,
  tooltip,
  rightElement,
  isReadOnly = false,
}: InputFormProps<FieldsType>) => {
  const translateMessage = useTranslateMessage();
  const { errors } = useFormState({ control });
  const errorMessage = get(errors, name + ".message") as unknown as string;
  const [isPasswordVisible, setIsPasswordVisible] = useState<boolean>();

  const ref = useRef<HTMLInputElement>(null);
  const [focus, setFocus] = useState(false);

  const togglePasswordVisibility = () => {
    setIsPasswordVisible((prevState) => {
      return !prevState;
    });
  };

  const getInput = (field: ControllerRenderProps<FieldsType, Path<FieldsType>>) => {
    const isInteractive = !disabled && !isReadOnly;

    if (type === "checkbox") {
      return (
        <div className="flex flex-row items-center gap-2">
          <input type={type} id={name} className="peer size-4" {...field} />
          <label
            htmlFor={name}
            className={clsx([
              "pointer-events-none flex text-sm text-gray-300 transition-all duration-200 ease-in-out",
            ])}
          >
            {title}
          </label>
        </div>
      );
    }

    return (
      <Field
        className={clsx(
          "relative flex h-14 w-full items-center rounded-lg border px-4 hover:border-gray-300",
          {
            "border-blue-600": focus,
            "cursor-not-allowed": !isInteractive,
            "bg-gray-50": !isInteractive,
          },
          !isInteractive ? "bg-gray-100" : "bg-white",
        )}
        onClick={() => {
          if (!isInteractive) return;
          setFocus(true);
          ref.current?.focus();
        }}
        onBlur={() => {
          if (!isInteractive) return;
          setFocus(false);
          ref.current?.blur();
        }}
      >
        <Label
          className={clsx([
            "pointer-events-none absolute flex overflow-hidden text-sm text-gray-500 transition-all duration-200 ease-in-out",
            {
              "text-xs":
                field.value?.length > 0 || (field.value !== "" && field.value !== undefined),
              "-translate-y-3":
                field.value?.length > 0 || (field.value !== "" && field.value !== undefined),
            },
          ])}
        >
          {title}
        </Label>
        {type === "number" ? (
          <Input
            {...field}
            ref={ref}
            id={name}
            readOnly={isReadOnly}
            onChange={(e) => {
              field.onChange(isFloat ? parseFloat(e.target.value) : parseInt(e.target.value));
            }}
            value={isNaN(field.value) ? "" : field.value}
            type="number"
            lang="fr"
            onWheel={(e) => {
              if (type === "number") {
                e.currentTarget.blur();
              }
            }}
            className={clsx("peer flex h-[80%] w-full bg-transparent focus:outline-hidden", {
              "border-red-500": errorMessage,
              "border-gray-300": !errorMessage,
              "translate-y-1.5": title && !isNaN(field.value),
              "cursor-not-allowed": !isInteractive,
            })}
          />
        ) : (
          <Input
            {...field}
            ref={ref}
            id={name}
            disabled={disabled}
            autoComplete="new-password"
            value={field.value || ""}
            readOnly={isReadOnly}
            type={type === "password" ? (isPasswordVisible ? "text" : "password") : type}
            className={clsx("peer flex h-[80%] w-full bg-transparent focus:outline-hidden", {
              "border-red-500": errorMessage,
              "border-gray-300": !errorMessage,
              "translate-y-1.5": title && field.value,
              "cursor-not-allowed": !isInteractive,
            })}
          />
        )}

        {rightElement && <div className="flex flex-row items-center">{rightElement}</div>}
        {tooltip && (
          <>
            <div className="has-tooltip self-center">
              <span className="tooltip bottom-3/4 w-max max-w-64 -translate-x-1/4 rounded-lg bg-blue-500 p-2 text-center text-sm font-semibold text-white shadow-lg">
                {tooltip}
              </span>
              <InformationCircleIcon className="size-6 text-blue-500" />
            </div>
          </>
        )}

        {type == "password" && (
          <div onClick={togglePasswordVisibility} className="absolute top-4 right-2 cursor-pointer">
            {isPasswordVisible ? (
              <EyeOffIcon className="text-gray-400" />
            ) : (
              <EyeIcon className="text-gray-400" />
            )}
          </div>
        )}
      </Field>
    );
  };

  return (
    <div className={clsx("flex flex-col gap-2", className)}>
      <Controller
        control={control}
        name={name}
        render={({ field }) => getInput(field)}
        defaultValue={defaultValue}
        disabled={disabled}
      />
      {errorMessage !== "" && (
        <p className="text-xs text-red-500">{translateMessage(errorMessage)}</p>
      )}
    </div>
  );
};

export default StyledInput;
